'use client';

import * as React from 'react';

import * as Sentry from '@sentry/nextjs';
import { customSessionClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

import type { BetterAuthInstance } from '../auth-betterauth';
import { clearOfflineData } from '../offline';

import { performInteractiveSignOutAction } from './interactiveSignOutAction';
import { redirectBrowserTo } from './redirectBrowserTo';
import { beginSignOut } from './signOutState';
import type { Session } from './types';

/**
 * The single sanctioned boundary to the Better Auth React client. Every
 * client-side session primitive the app consumes is re-exported here, so
 * consumers depend on this app-owned seam via
 * `@cntxt/shared/infrastructure/session` — never on `better-auth` directly
 * (ADR-0001 Task 04, ADR-0004). The shim maps Better Auth's shapes onto the
 * app's {@link Session} contract so the ~65 downstream consumers are unchanged.
 *
 * No `baseURL` is set: the client calls the same-origin `/api/auth`, so each
 * tenant host talks to its own auth surface (ADR-0006).
 */
const authClient = createAuthClient({
  plugins: [customSessionClient<BetterAuthInstance>()],
});

type SessionStatus = 'authenticated' | 'unauthenticated' | 'loading';

/**
 * Loosened stand-in for next-auth's `UseSessionOptions`, kept (now inert) so
 * existing `useSession(options)` / `useUser(options)` call sites compile
 * unchanged.
 */
export type UseSessionOptions = {
  required?: boolean;
  onUnauthenticated?: () => void;
};

type UseSessionResult = {
  data: Session | null;
  status: SessionStatus;
  isPending: boolean;
  error: unknown;
  /** Re-reads the session from the server (replaces next-auth's `update`). */
  refetch: () => Promise<void>;
};

/**
 * Session hook mapped to the next-auth-shaped `{ data, status }` contract. The
 * enriched Application Session rides `data` (via the `customSession` plugin);
 * `status` is derived from Better Auth's `isPending`/`data`. There is no
 * `update` — consumers never relied on it.
 */
export function useSession(_options?: UseSessionOptions): UseSessionResult {
  const { data, isPending, error, refetch } = authClient.useSession();
  const status: SessionStatus = isPending
    ? 'loading'
    : data
      ? 'authenticated'
      : 'unauthenticated';

  return {
    // The server `customSession` plugin makes `data` the enriched Application
    // Session at runtime; the client type can't infer that off the opaque
    // instance, so bridge it explicitly.
    data: (data as unknown as Session | null) ?? null,
    status,
    isPending,
    error,
    refetch: () => refetch(),
  };
}

/** Resolves the current Application Session (or `null`) outside React. */
export async function getSession(): Promise<Session | null> {
  const { data } = await authClient.getSession();
  return (data as unknown as Session | null) ?? null;
}

/**
 * Appends the trace's sentry-trace/baggage as query params, since a top-level
 * redirect can't carry headers - the Keycloak login page reads them back off
 * its own URL to continue this trace instead of starting a new one.
 *
 * `url` should always be absolute (Better Auth's own sign-in route builds it via
 * the same `new URL()` call, which would throw server-side first otherwise), but
 * degrade to no tracing rather than block sign-in if that ever stops holding.
 */
export function appendTraceParams(
  url: string,
  traceData: ReturnType<typeof Sentry.getTraceData>,
): string {
  let authorizationUrl: URL;
  try {
    authorizationUrl = new URL(url);
  } catch {
    return url;
  }
  if (traceData['sentry-trace']) {
    authorizationUrl.searchParams.set(
      'sentry-trace',
      traceData['sentry-trace'],
    );
  }
  if (traceData.baggage) {
    authorizationUrl.searchParams.set('baggage', traceData.baggage);
  }
  return authorizationUrl.toString();
}

/**
 * The sign-in this document has already started, if any. See {@link signIn}.
 */
let pendingSignIn: Promise<unknown> | null = null;

/**
 * Starts the Keycloak OAuth sign-in for a tenant. `provider` is the tenant name
 * (the genericOAuth provider id, which Better Auth registers as a social
 * provider); `redirectTo` becomes the post-login callback.
 *
 * `disableRedirect: true` stops Better Auth's own redirect plugin so we can run
 * the returned URL through {@link appendTraceParams} before navigating.
 */
async function startSignIn(
  provider: string,
  options?: { redirectTo?: string },
): Promise<unknown> {
  const result = await authClient.signIn.social({
    provider,
    callbackURL: options?.redirectTo,
    disableRedirect: true,
  });

  const url = result.data?.url;
  if (!url) {
    // Better Auth resolves `{ data, error }` instead of throwing — reject here
    // so callers' `.catch(reportUnexpectedSignInRejection)` runs.
    throw result.error instanceof Error
      ? result.error
      : new Error(
          (result.error as { message?: string } | undefined)?.message ??
            'OAuth sign-in did not return an authorization URL',
        );
  }

  try {
    redirectBrowserTo(appendTraceParams(url, Sentry.getTraceData()));
  } catch {
    // Tracing is best-effort — never block sign-in if Sentry is unavailable.
    redirectBrowserTo(url);
  }
  return result;
}

/**
 * Starts sign-in once per document, joining any attempt already under way.
 *
 * Each call to `/api/auth/sign-in/social` mints a fresh OAuth state and
 * overwrites the one host-scoped `state` cookie. A second call started before
 * the browser has left for Keycloak therefore leaves that cookie holding a state
 * the authorization URL in flight does not carry, and the callback rejects it as
 * `state_mismatch` (PSI-17427). Both triggers (`AuthProvider`,
 * `RedirectToKeycloak`) run from effects that can re-run, and the window stays
 * open from the request until the navigation commits, long enough to hit on a
 * slow connection.
 *
 * A resolved attempt is kept: the browser is on its way to Keycloak, and a later
 * caller must not mint a second state behind it. A failed one is released, so a
 * genuine retry can still start.
 */
export function signIn(
  provider: string,
  options?: { redirectTo?: string },
): Promise<unknown> {
  pendingSignIn ??= startSignIn(provider, options).catch((error: unknown) => {
    pendingSignIn = null;
    throw error;
  });

  return pendingSignIn;
}

/** Test seam: forgets the started sign-in so a fresh one can begin. */
export function resetPendingSignIn(): void {
  pendingSignIn = null;
}

/**
 * Interactive (front-channel) sign-out (decision B/3). While still
 * authenticated it resolves the host-derived Keycloak end-session URL, deletes
 * the Better Auth session (firing the back-channel `session.delete` hook), then
 * redirects the browser through Keycloak's end-session endpoint so Keycloak
 * clears its own SSO cookie. Falls back to a plain redirect when there is no
 * front-channel URL (no linked account / no `id_token`).
 *
 * Accepts `redirectTo` (or the legacy `callbackUrl`) as the fallback target.
 */
export type SignOutOptions = { redirectTo?: string; callbackUrl?: string };

/**
 * Chooses the sign-out redirect: the front-channel Keycloak end-session URL when
 * one was resolved, else the caller's `redirectTo` (or legacy `callbackUrl`),
 * else the current origin.
 */
export function resolveSignOutTarget(
  ssoLogoutUrl: string | null,
  options: SignOutOptions | undefined,
  defaultTarget: string,
): string {
  return (
    ssoLogoutUrl ?? options?.redirectTo ?? options?.callbackUrl ?? defaultTarget
  );
}

export async function signOut(options?: SignOutOptions): Promise<void> {
  // Drop the page tree for a neutral loader (via the app shell's
  // `useIsSigningOut`) *before* tearing the session down, so no still-mounted
  // component re-renders against an empty session and flashes a full-screen
  // error in the window before the redirect lands.
  beginSignOut();

  let ssoLogoutUrl: string | null = null;
  try {
    ({ frontChannelLogoutUrl: ssoLogoutUrl } =
      await performInteractiveSignOutAction());
  } catch {
    // Never block sign-out on a failed SSO resolver round-trip.
    ssoLogoutUrl = null;
  }

  await authClient.signOut();
  await clearOfflineData();

  redirectBrowserTo(
    resolveSignOutTarget(ssoLogoutUrl, options, window.location.origin),
  );
}

/**
 * Passthrough provider. The Better Auth React client is a global store and needs
 * no context provider, so this simply renders its children — keeping the app's
 * `<SessionProvider>` mount points (and their now-inert props) unchanged.
 */
export function SessionProvider(
  props: React.PropsWithChildren<Record<string, unknown>>,
): React.ReactNode {
  return props.children;
}
